import styles from './styles.module.css'

import { AiOutlineQuestionCircle } from '@react-icons/all-files/ai/AiOutlineQuestionCircle'
import React, { useCallback, useState } from 'react'

import ThumbsUpSvg from 'assets/icons/thumbs-up.svg'

import { Flex, IconButton, Separator, Text, TextArea } from 'components/ds'

export type FeedbackRating = boolean | null
export type RightsDataUsageFeedback = {
  isHelpful: FeedbackRating
  comment?: string
}

type Props = {
  onFeedbackChange: (feedback: RightsDataUsageFeedback) => void
}

const RightsDataFeedback = ({ onFeedbackChange }: Props) => {
  const [isHelpful, setIsHelpful] = useState<FeedbackRating>(null)
  const [comment, setComment] = useState('')

  const handleIsHelpfulChange = useCallback(
    (newIsHelpful: FeedbackRating) => {
      const updatedIsHelpful = isHelpful === newIsHelpful ? null : newIsHelpful
      setIsHelpful(updatedIsHelpful)
      const updatedComment = updatedIsHelpful ? '' : comment
      onFeedbackChange({
        isHelpful: updatedIsHelpful,
        comment: updatedComment,
      })
    },
    [isHelpful, comment, onFeedbackChange],
  )

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newComment = e.target.value
      setComment(newComment)
      onFeedbackChange({
        isHelpful,
        comment: newComment,
      })
    },
    [isHelpful, onFeedbackChange],
  )

  return (
    <Flex
      direction="column"
      gap="3"
      mb="1"
      mt="3"
      p="4"
      className={styles.container}
    >
      <Flex gap="3" align="center">
        <AiOutlineQuestionCircle />
        <Text size="2" weight="medium" className={styles.questionText}>
          Was this rights data accurate and complete?
        </Text>
        <Flex gap="2" justify="end">
          <IconButton
            ml="1"
            mr="1"
            variant="ghost"
            size="1"
            color="gray"
            className={isHelpful === true ? styles.activeButton : undefined}
            onClick={() => handleIsHelpfulChange(true)}
            aria-label="Thumbs up - data is accurate"
          >
            <ThumbsUpSvg width={16} height={16} />
          </IconButton>
          <IconButton
            ml="1"
            mr="1"
            variant="ghost"
            size="1"
            color="gray"
            className={isHelpful === false ? styles.activeButton : undefined}
            onClick={() => handleIsHelpfulChange(false)}
            aria-label="Thumbs down - data needs improvement"
          >
            <ThumbsUpSvg
              width={16}
              height={16}
              style={{
                transform: 'rotate(180deg)',
              }}
            />
          </IconButton>
        </Flex>
      </Flex>
      {isHelpful === false && (
        <>
          <Separator size="4" />
          <Flex direction="column" gap="1">
            <Text as="label" size="2" weight="medium">
              What&apos;s wrong with the data? (Optional)
            </Text>
            <TextArea
              variant="surface"
              size="2"
              placeholder="Please describe what information is missing or incorrect..."
              value={comment}
              onChange={handleCommentChange}
              className={styles.textArea}
            />
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default RightsDataFeedback
